import CentricDispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes } from '../constants/CentricConstants';
import EventEmitter from 'events';
import assign from 'object-assign';

const CHANGE_EVENT = 'change';
const FORM_CHANGE_EVENT = 'form_change';

let _categories = [];
let _tree = [];
let _dirty = true;
let _dirtyTotals = true;

const rebuildTree = () => {
    const findChildren = (parent) => {
        const children = _categories.filter((e) => (e !== undefined && e.parentId === parent.id)).sort((a, b) => a.name.localeCompare(b.name));
        parent.children = children.map(findChildren);
        return parent;
    };

    const roots = _categories.filter((e) => (e !== undefined && !e.parentId)).sort((a, b) => a.name.localeCompare(b.name));
    _tree = roots.map(findChildren);
    _dirty = false;
};

const sanitizeEntity = ({id, parentId, ...entity}) => ({
    ...entity,
    id: Number(id),
    parentId: Number(parentId) || null,
    courses: Number(entity.courses) || null,
    modules: Number(entity.modules) || null,
    resources: Number(entity.resources) || null,
    canAddContent: !!Number(entity.canAddContent),
    canAddSubcategories: !!Number(entity.canAddSubcategories),
    canEdit: !!Number(entity.canEdit)
});

const setContentTotals = () => {
    const sumChildContent = (parent) => {
        let childCourses = 0, childModules = 0, childResources = 0;
        const children = _categories.filter((e) => (e !== undefined && e.parentId === parent.id));
        children.forEach((child) => {
            sumChildContent(child);
            childCourses += child.totalCourses;
            childModules += child.totalModules;
            childResources += child.totalResources;
        });
        parent.totalCourses = parent.courses + childCourses;
        parent.totalModules = parent.modules + childModules;
        parent.totalResources = parent.resources + childResources;
    };

    _categories.filter((c) => (c !== undefined && c.parentId === null)).forEach(sumChildContent);
    _dirtyTotals = false;
};

let CategoryStore = assign({}, EventEmitter.prototype, {

    emitChange: function() {
        this.emit(CHANGE_EVENT);
    },
    addListener: function(callback) {
        this.addChangeListener(callback);
        return { remove: () => this.removeChangeListener(callback) };
    },
    addChangeListener: function(callback) {
        this.on(CHANGE_EVENT, callback);
    },
    removeChangeListener: function(callback) {
        this.removeListener(CHANGE_EVENT, callback);
    },
    addFormChangeListener: function(callback) {
        this.on(FORM_CHANGE_EVENT, callback);
    },

    getAll: function() {
        if (_dirtyTotals) { setContentTotals() }
        return _categories.filter((e) => e !== undefined);
    },

    getTree: function() {
        if (_dirty) { rebuildTree(); }
        return _tree;
    },

    getRoots: function() {
        if (_dirtyTotals) { setContentTotals() }
        return _categories.filter((c) => (c !== undefined && c.parentId === null));
    },

    getById: function(id) {
        if (_dirtyTotals) { setContentTotals() }
        id = Number(id);
        return _categories.find((e) => (e !== undefined && e.id === id));
    },

    getByParent: function(id) {
        if (_dirtyTotals) { setContentTotals() }
        id = Number(id);
        return _categories.filter((e) => (e !== undefined && e.parentId === id));
    },

    getAncestors: (id) => {
        let ancestors = [];
        const findAncestors = (categoryId) => {
            const category = CategoryStore.getById(categoryId);
            ancestors.unshift(category);
            if (!!category.parentId) {
                findAncestors(category.parentId);
            }
        };
        findAncestors(id);
        return ancestors;
    },

    add: function(entity) {
        entity = sanitizeEntity(entity);
        const index = _categories.findIndex((e) => ( e !== undefined && e.id === entity.id ));
        if (0 <= index) {
            _categories[index] = entity;
        } else {
            _categories.push(entity);
        }
        _dirty = true;
    },

    delete: function(id) {
        id = Number(id);
        let index = _categories.findIndex((e) => e !== undefined && e.id === id);
        if (0 <= index) {
            delete _categories[index];
            _dirty = true;
        }
    },

});

CategoryStore.dispatchToken = CentricDispatcher.register(function(action) {

    switch(action.type) {

        case ActionTypes.CATEGORY_INFO_SUCCESS:
            CategoryStore.add(action.data);
            _dirtyTotals = true;
            CategoryStore.emitChange();
            break;

        case ActionTypes.CATEGORY_LIST:
            action.data.forEach(CategoryStore.add);
            _dirtyTotals = true;
            CategoryStore.emitChange();
            break;

        case ActionTypes.CATEGORY_LIST_ERROR:
            CategoryStore.emitChange();
            break;

        case ActionTypes.CATEGORY_INFO_ERROR:
            CategoryStore.emitChange();
            break;

        case ActionTypes.CATEGORY_SAVE:
            CategoryStore.add(action.data);
            _dirtyTotals = true;
            CategoryStore.emitChange();
            break;

        case ActionTypes.CATEGORY_UPDATE:
            CategoryStore.add(action.data);
            _dirtyTotals = true;
            CategoryStore.emitChange();
            break;

        case ActionTypes.CATEGORY_DELETE:
            if (!!action.data.id && !!action.data.deleted) {
                CategoryStore.delete(action.data.id);
                _dirtyTotals = true;
            }
            CategoryStore.emitChange();
            break;

        case ActionTypes.CATEGORY_ROOT_LIST_SUCCESS:
            action.data.forEach(CategoryStore.add);
            _dirtyTotals = true;
            CategoryStore.emitChange();
            break;

        case ActionTypes.CATEGORY_ROOT_LIST_FAILURE:
            CategoryStore.emitChange();
            break;

        default:
            break;
    }

});

export default CategoryStore;
